import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  getExpeditionFields, createExpeditionField, updateExpeditionField, deleteExpeditionField,
  getExpeditionEntries, createExpeditionEntry, deleteExpeditionEntry, getExpeditionAnalytics,
  exportExpedition
} from '@/services/api';
import html2canvas from 'html2canvas';
import { perfColor } from '@/utils/styleHelpers';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Plus, Trash2, Edit, Save, X, Truck, Package,
  ArrowDownToLine, ArrowUpFromLine, Settings, RefreshCw, Info, Check,
  LayoutDashboard, Clock, Target, Activity, Download, Mail, Camera
} from 'lucide-react';

const FIELD_TYPES = [
  { value: 'timestamp', label: 'Timestamp' },
  { value: 'duration', label: 'Duration (minutes)' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'select', label: 'Select (Yes/No)' },
  { value: 'datetime', label: 'Date & Time' },
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
];

const COLORS = ['#00CED1', '#2372D4', '#10b981', '#f59e0b', '#ef4444'];

const formatFieldValue = (value, fieldType) => {
  if (!value || value === '') return '-';
  if (fieldType === 'checkbox') return value === 'true' ? 'Yes' : 'No';
  if (fieldType === 'select') return value;
  if (fieldType === 'timestamp' || fieldType === 'datetime') {
    try {
      const d = new Date(value);
      return d.toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return value; }
  }
  if (fieldType === 'duration') return `${value} min`;
  return value;
};

const FieldInput = ({ field, value, onChange, disabled }) => {
  switch (field.field_type) {
    case 'select':
      return (
        <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger className="h-9 text-sm" data-testid={`field-${field.field_id}`}>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {(field.options || ['Yes', 'No']).map(opt => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'checkbox':
      return (
        <div className="flex items-center h-10">
          <Checkbox
            checked={value === 'true'}
            onCheckedChange={(checked) => onChange(checked ? 'true' : 'false')}
            disabled={disabled}
            data-testid={`field-${field.field_id}`}
          />
        </div>
      );
    case 'timestamp':
    case 'datetime':
      return (
        <Input
          type="datetime-local"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="h-9 text-sm"
          data-testid={`field-${field.field_id}`}
        />
      );
    case 'duration':
      return (
        <Input
          type="number"
          placeholder="minutes"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="h-9 text-sm"
          data-testid={`field-${field.field_id}`}
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="h-9 text-sm"
          data-testid={`field-${field.field_id}`}
        />
      );
    default:
      return (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="h-9 text-sm"
          data-testid={`field-${field.field_id}`}
        />
      );
  }
};

// Helper to download a blob with mailto option
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const mailtoExport = (filename) => {
  const subject = encodeURIComponent(`KAIZEN KANRI - ${filename}`);
  const body = encodeURIComponent(`Hi,\n\nPlease find the attached export: ${filename}\n\nGenerated from KAIZEN KANRI Expedition.\n\nBest regards`);
  window.open(`mailto:?subject=${subject}&body=${body}`);
};

// Dashboard Analytics Component
const DashboardTab = ({ analytics, loading, isGuest, dashboardRef }) => {
  if (loading || !analytics) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statusData = [
    { name: 'Active Inbound', value: analytics.active_inbound, fill: '#3b82f6' },
    { name: 'Completed Inbound', value: analytics.completed_inbound, fill: '#10b981' },
    { name: 'Active Outbound', value: analytics.active_outbound, fill: '#f59e0b' },
    { name: 'Completed Outbound', value: analytics.completed_outbound, fill: '#8b5cf6' },
  ].filter(d => d.value > 0);

  const barData = [
    { name: 'Avg Unloading', value: analytics.avg_unloading_time, unit: 'min', fill: '#3b82f6' },
    { name: 'Avg Dock Time', value: analytics.avg_dock_time, unit: 'min', fill: '#10b981' },
    { name: 'Avg Loading', value: analytics.avg_loading_time, unit: 'min', fill: '#f59e0b' },
  ];

  return (
    <div ref={dashboardRef} className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="kpi-avg-unloading">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.avg_unloading_time}<span className="text-sm font-normal text-muted-foreground ml-1">min</span></p>
                <p className="text-xs text-muted-foreground">Avg Unloading Time</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="kpi-departure-rate">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Target className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${perfColor(analytics.on_time_departure_rate, 90, 70)}`}>
                  {analytics.on_time_departure_rate}%
                </p>
                <p className="text-xs text-muted-foreground">Departure Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="kpi-dock-time">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Activity className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.avg_dock_time}<span className="text-sm font-normal text-muted-foreground ml-1">min</span></p>
                <p className="text-xs text-muted-foreground">Avg Dock Time</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="kpi-total-entries">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.total_entries}</p>
                <p className="text-xs text-muted-foreground">Total Shipments</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-lg font-bold text-blue-600">{analytics.total_inbound}</p>
            <p className="text-xs text-muted-foreground">Inbound</p>
            <div className="mt-2 flex gap-2 justify-center text-xs">
              <span className="text-emerald-600">{analytics.completed_inbound} done</span>
              <span className="text-amber-600">{analytics.active_inbound} active</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-lg font-bold text-emerald-600">{analytics.total_outbound}</p>
            <p className="text-xs text-muted-foreground">Outbound</p>
            <div className="mt-2 flex gap-2 justify-center text-xs">
              <span className="text-emerald-600">{analytics.completed_outbound} done</span>
              <span className="text-amber-600">{analytics.active_outbound} active</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className={`text-lg font-bold ${analytics.correct_dock_rate >= 90 ? 'text-emerald-600' : 'text-amber-600'}`}>{analytics.correct_dock_rate}%</p>
            <p className="text-xs text-muted-foreground">Correct Dock Rate</p>
            <Progress value={analytics.correct_dock_rate} className="h-1.5 mt-2" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className={`text-lg font-bold ${analytics.order_change_rate <= 10 ? 'text-emerald-600' : 'text-amber-600'}`}>{analytics.order_change_rate}%</p>
            <p className="text-xs text-muted-foreground">Order Change Rate</p>
            <Progress value={analytics.order_change_rate} className="h-1.5 mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Average Times</CardTitle>
            <CardDescription>Unloading, dock, and loading durations (minutes)</CardDescription>
          </CardHeader>
          <CardContent>
            {barData.some(d => d.value > 0) ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(val) => [`${val} min`, 'Duration']} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {barData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">No time data available</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Shipment Status</CardTitle>
            <CardDescription>Active vs completed breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={4}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {statusData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">No data available</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const ExpeditionPage = () => {
  const { isAdmin, isMasterAdmin, isGuest } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [fields, setFields] = useState([]);
  const [entries, setEntries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editingFieldName, setEditingFieldName] = useState(null);
  const [newFieldName, setNewFieldName] = useState('');
  const [newField, setNewField] = useState({ name: '', field_type: 'text', applies_to: 'inbound' });
  const [newEntry, setNewEntry] = useState({ shipment_id: '', truck_number: '', fields: {} });
  const dashboardRef = useRef(null);

  const entryTab = activeTab === 'inbound' || activeTab === 'outbound' ? activeTab : null;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const [fieldsData, analyticsData] = await Promise.all([
          getExpeditionFields(),
          getExpeditionAnalytics()
        ]);
        setFields(fieldsData);
        setAnalytics(analyticsData);
        setEntries([]);
      } else {
        const [fieldsData, entriesData] = await Promise.all([
          getExpeditionFields(),
          getExpeditionEntries(entryTab)
        ]);
        setFields(fieldsData);
        setEntries(entriesData);
      }
    } catch (error) {
      toast.error('Failed to load expedition data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, entryTab]);

  useEffect(() => { loadData(); }, [loadData]);

  const currentFields = fields.filter(f => f.applies_to === (entryTab || 'inbound'));

  const handleCreateEntry = async () => {
    const tabForCreate = entryTab || 'inbound';
    const fieldsForTab = fields.filter(f => f.applies_to === tabForCreate);

    // Validate: if Order Change = Yes, order change time is required
    if (tabForCreate === 'inbound') {
      const ocVal = newEntry.fields['f_label_order_change'];
      if (ocVal === 'Yes' && !newEntry.fields['f_order_change_datetime']) {
        toast.error('Order Change Time is required when Order Change is Yes');
        return;
      }
    }

    try {
      const processedFields = { ...newEntry.fields };
      for (const f of fieldsForTab) {
        if ((f.field_type === 'timestamp' || f.field_type === 'datetime') && processedFields[f.field_id]) {
          try {
            processedFields[f.field_id] = new Date(processedFields[f.field_id]).toISOString();
          } catch { /* keep as-is */ }
        }
      }

      // If Order Change = No, task finishes at Drive Away time
      if (tabForCreate === 'inbound' && processedFields['f_label_order_change'] !== 'Yes') {
        processedFields['f_order_change_datetime'] = '';
      }

      await createExpeditionEntry({ ...newEntry, type: tabForCreate, fields: processedFields });
      setCreateOpen(false);
      setNewEntry({ shipment_id: '', truck_number: '', fields: {} });
      loadData();
      toast.success('Entry created');
    } catch (error) {
      toast.error(error.message || 'Failed to create entry');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await deleteExpeditionEntry(entryId);
      loadData();
      toast.success('Entry deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete entry');
    }
  };

  const handleAddField = async () => {
    if (!newField.name.trim()) return;
    try {
      await createExpeditionField({ ...newField, applies_to: entryTab || newField.applies_to });
      setNewField({ name: '', field_type: 'text', applies_to: entryTab || 'inbound' });
      setFieldDialogOpen(false);
      loadData();
      toast.success('Field added');
    } catch (error) {
      toast.error(error.message || 'Failed to add field');
    }
  };

  const handleRenameField = async (fieldId) => {
    if (!newFieldName.trim()) return;
    try {
      await updateExpeditionField(fieldId, { name: newFieldName });
      setEditingFieldName(null);
      setNewFieldName('');
      loadData();
      toast.success('Field renamed');
    } catch (error) {
      toast.error(error.message || 'Failed to rename field');
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Remove this field?')) return;
    try {
      await deleteExpeditionField(fieldId);
      loadData();
      toast.success('Field removed');
    } catch (error) {
      toast.error(error.message || 'Failed to remove field');
    }
  };

  const toLocalDatetime = (isoStr) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
  };

  const handleExportEntries = async (type) => {
    try {
      const blob = await exportExpedition(type);
      downloadBlob(blob, `expedition_${type}.xlsx`);
      toast.success(`${type} entries exported`);
    } catch (error) {
      toast.error(error.message || 'Export failed');
    }
  };

  const handleExportDashboardPNG = async () => {
    if (!dashboardRef.current) return;
    try {
      const canvas = await html2canvas(dashboardRef.current, { useCORS: true, scale: 2, backgroundColor: '#ffffff' });
      canvas.toBlob((blob) => {
        downloadBlob(blob, 'expedition_dashboard.png');
        toast.success('Dashboard exported as PNG');
      });
    } catch (error) {
      toast.error('Failed to capture dashboard');
    }
  };

  const renderEntryTable = (tabType) => {
    const tabFields = fields.filter(f => f.applies_to === tabType);
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              {tabType === 'inbound' ? <ArrowDownToLine className="h-5 w-5 text-blue-500" /> : <ArrowUpFromLine className="h-5 w-5 text-emerald-500" />}
              {tabType === 'inbound' ? 'Inbound Shipments' : 'Outbound Shipments'}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{entries.length} entries</Badge>
              <Button variant="outline" size="sm" onClick={() => handleExportEntries(tabType)} data-testid={`export-${tabType}-btn`}>
                <Download className="h-3.5 w-3.5 mr-1.5" /> Excel
              </Button>
              <Button variant="ghost" size="sm" onClick={() => mailtoExport(`expedition_${tabType}.xlsx`)} data-testid={`mailto-${tabType}-btn`}>
                <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isAdmin && tabFields.length > 0 && (
            <div className="mb-4 p-3 bg-muted/40 rounded-md">
              <p className="text-xs text-muted-foreground mb-2 font-medium">Field Management (Admin)</p>
              <div className="flex flex-wrap gap-2">
                {tabFields.map(f => (
                  <div key={f.field_id} className="flex items-center gap-1">
                    {editingFieldName === f.field_id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={newFieldName}
                          onChange={(e) => setNewFieldName(e.target.value)}
                          className="h-7 w-32 text-xs"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRenameField(f.field_id); if (e.key === 'Escape') setEditingFieldName(null); }}
                          data-testid={`rename-field-input-${f.field_id}`}
                        />
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleRenameField(f.field_id)}>
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditingFieldName(null)}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <Badge variant="secondary" className="text-xs cursor-default flex items-center gap-1">
                        {f.name}
                        <button onClick={() => { setEditingFieldName(f.field_id); setNewFieldName(f.name); }} className="ml-1 hover:text-blue-600" data-testid={`edit-field-name-${f.field_id}`}>
                          <Edit className="h-2.5 w-2.5" />
                        </button>
                        {!f.is_default && (
                          <button onClick={() => handleDeleteField(f.field_id)} className="ml-0.5 hover:text-red-600" data-testid={`delete-field-${f.field_id}`}>
                            <X className="h-2.5 w-2.5" />
                          </button>
                        )}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {entries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`expedition-table-${tabType}`}>
                <thead>
                  <tr className="border-b border-muted text-left">
                    <th className="pb-3 pr-3 font-medium text-muted-foreground">Route No.</th>
                    <th className="pb-3 pr-3 font-medium text-muted-foreground">Truck (Reg.)</th>
                    {tabFields.map(f => (
                      <th key={f.field_id} className="pb-3 pr-3 font-medium text-muted-foreground whitespace-nowrap">{f.name}</th>
                    ))}
                    {isAdmin && <th className="pb-3 font-medium text-muted-foreground text-center">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {entries.map(entry => (
                    <tr key={entry.entry_id} className="border-b border-muted/50 hover:bg-muted/20" data-testid={`expedition-row-${entry.entry_id}`}>
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{entry.shipment_id || '-'}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-2">
                          <Truck className="h-4 w-4 text-muted-foreground" />
                          <span>{entry.truck_number || '-'}</span>
                        </div>
                      </td>
                      {tabFields.map(f => (
                        <td key={f.field_id} className="py-3 pr-3 whitespace-nowrap">
                          {f.field_type === 'checkbox' ? (
                            entry.fields?.[f.field_id] === 'true' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                                <Check className="h-3 w-3 mr-1" /> Yes
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">No</Badge>
                            )
                          ) : f.field_type === 'select' ? (
                            entry.fields?.[f.field_id] === 'Yes' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                                <Check className="h-3 w-3 mr-1" /> Yes
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">No</Badge>
                            )
                          ) : (
                            <span className={entry.fields?.[f.field_id] ? '' : 'text-muted-foreground'}>
                              {formatFieldValue(entry.fields?.[f.field_id], f.field_type)}
                            </span>
                          )}
                        </td>
                      ))}
                      {isAdmin && (
                        <td className="py-3 text-center">
                          <Button
                            variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:text-rose-700"
                            onClick={() => handleDeleteEntry(entry.entry_id)}
                            data-testid={`delete-entry-${entry.entry_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              {tabType === 'inbound' ? <ArrowDownToLine className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" /> : <ArrowUpFromLine className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />}
              <p className="text-muted-foreground">No {tabType} entries yet</p>
              {!isGuest && (
                <Button variant="outline" className="mt-3" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Add first entry
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Fields for the create dialog - use current entryTab or default to inbound
  const createTabType = entryTab || 'inbound';
  const createFields = fields.filter(f => f.applies_to === createTabType);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="expedition-page">
        {isGuest && (
          <div className="flex items-center gap-3 px-4 py-3 bg-sky-50 border border-sky-200 rounded-lg" data-testid="demo-data-banner">
            <Info className="h-5 w-5 text-sky-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-sky-800">Sample Data Preview</p>
              <p className="text-xs text-sky-600">The entries below are illustrative examples.</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Expedition</h1>
            <p className="text-muted-foreground mt-1">Track inbound and outbound shipments</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={loadData} data-testid="refresh-expedition">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            {isAdmin && entryTab && (
              <Button variant="outline" onClick={() => setFieldDialogOpen(true)} data-testid="manage-fields-btn">
                <Settings className="h-4 w-4 mr-2" /> Add Field
              </Button>
            )}
            {!isGuest && (
              <Button onClick={() => { if (!entryTab) setActiveTab('inbound'); setCreateOpen(true); }} data-testid="new-entry-btn">
                <Plus className="h-4 w-4 mr-2" /> New Entry
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="inbound" data-testid="tab-inbound">
              <ArrowDownToLine className="h-4 w-4 mr-2" /> Inbound
            </TabsTrigger>
            <TabsTrigger value="outbound" data-testid="tab-outbound">
              <ArrowUpFromLine className="h-4 w-4 mr-2" /> Outbound
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6">
            <div className="flex justify-end gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={handleExportDashboardPNG} data-testid="export-dashboard-png">
                <Camera className="h-3.5 w-3.5 mr-1.5" /> Export PNG
              </Button>
              <Button variant="ghost" size="sm" onClick={() => mailtoExport('expedition_dashboard.png')} data-testid="mailto-dashboard-btn">
                <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
              </Button>
            </div>
            <DashboardTab analytics={analytics} loading={loading} isGuest={isGuest} dashboardRef={dashboardRef} />
          </TabsContent>

          <TabsContent value="inbound" className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : renderEntryTable('inbound')}
          </TabsContent>

          <TabsContent value="outbound" className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : renderEntryTable('outbound')}
          </TabsContent>
        </Tabs>

        {/* Create Entry Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-[480px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle data-testid="create-entry-dialog-title">
                New {createTabType === 'inbound' ? 'Inbound' : 'Outbound'} Entry
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Route Number</Label>
                  <Input
                    value={newEntry.shipment_id}
                    onChange={(e) => setNewEntry(p => ({...p, shipment_id: e.target.value}))}
                    placeholder="RT-2026-..."
                    className="h-9 text-sm"
                    data-testid="entry-shipment-id"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Truck (Registration)</Label>
                  <Input
                    value={newEntry.truck_number}
                    onChange={(e) => setNewEntry(p => ({...p, truck_number: e.target.value}))}
                    placeholder="NL-AB-123"
                    className="h-9 text-sm"
                    data-testid="entry-truck-number"
                  />
                </div>
              </div>

              <div className="border-t pt-3">
                <p className="text-sm font-medium mb-3">
                  {createTabType === 'inbound' ? 'Inbound' : 'Outbound'} Fields
                </p>
                <div className="space-y-3">
                  {createFields.map(f => {
                    // Order Change Time: disabled when Order Change = No
                    const isOrderChangeTime = f.field_id === 'f_order_change_datetime';
                    const orderChangeValue = newEntry.fields['f_label_order_change'];
                    const isDisabled = isOrderChangeTime && orderChangeValue !== 'Yes';

                    return (
                      <div key={f.field_id} className="flex items-center gap-3">
                        <Label className={`text-xs w-40 flex-shrink-0 ${isDisabled ? 'text-muted-foreground/50' : ''}`}>{f.name}</Label>
                        <div className="flex-1">
                          <FieldInput
                            field={f}
                            value={f.field_type === 'timestamp' || f.field_type === 'datetime'
                              ? toLocalDatetime(newEntry.fields[f.field_id])
                              : newEntry.fields[f.field_id] || ''}
                            onChange={(val) => {
                              const updates = { [f.field_id]: val };
                              // When Order Change = No, clear order change time
                              if (f.field_id === 'f_label_order_change' && val === 'No') {
                                updates['f_order_change_datetime'] = '';
                              }
                              setNewEntry(p => ({...p, fields: {...p.fields, ...updates}}));
                            }}
                            disabled={isDisabled}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button
                onClick={handleCreateEntry}
                className="w-full"
                disabled={!newEntry.shipment_id.trim()}
                data-testid="save-entry-btn"
              >
                <Save className="h-4 w-4 mr-2" /> Save Entry
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Field Dialog */}
        <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Add Custom Field to {entryTab === 'outbound' ? 'Outbound' : 'Inbound'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Field Name</Label>
                <Input
                  value={newField.name}
                  onChange={(e) => setNewField(p => ({...p, name: e.target.value}))}
                  placeholder="e.g., Temperature Check"
                  data-testid="new-field-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Field Type</Label>
                <Select value={newField.field_type} onValueChange={(v) => setNewField(p => ({...p, field_type: v}))}>
                  <SelectTrigger data-testid="new-field-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(ft => (
                      <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleAddField} className="w-full" disabled={!newField.name.trim()} data-testid="save-field-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Field
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ExpeditionPage;
